/**
 * Hook to update an ABR filter (full replacement).
 *
 * Follows the same refetch pattern as useUpdateCommunityPolicy.
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { AbrFilter, AbrFilterCriteriaInput } from '@moderation/domain/AbrFilter';
import { UPDATE_ABR_FILTER, GET_ABR_FILTERS } from '@moderation/graphql';
import { useCallback, useMemo, useState } from 'react';

interface UpdateParams {
  filterId: string;
  name: string;
  description?: string;
  criteria: AbrFilterCriteriaInput;
}

interface UseUpdateAbrFilterResult {
  updateFilter: (params: UpdateParams) => Promise<AbrFilter | null>;
  loading: boolean;
  errors: DomainError[] | null;
  resetErrors: () => void;
}

export function useUpdateAbrFilter(communityId: string): UseUpdateAbrFilterResult {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [executeMutation, { loading }] = useMutation(UPDATE_ABR_FILTER, {
    refetchQueries: communityId ? [{ query: GET_ABR_FILTERS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const updateFilter = useCallback(
    async (params: UpdateParams): Promise<AbrFilter | null> => {
      try {
        setErrors(null);

        track('UpdateAbrFilterInitiated', { communityId, filterId: params.filterId });

        const result = await executeMutation({
          variables: {
            input: {
              communityId,
              filterId: params.filterId,
              name: params.name,
              description: params.description,
              criteria: params.criteria,
            },
          },
        });

        const updated = result.data?.updateAbrFilter ?? null;

        if (updated) {
          track('UpdateAbrFilterSucceeded', { communityId, filterId: params.filterId });
        } else {
          track('UpdateAbrFilterFailed', { communityId, filterId: params.filterId, errorMessage: 'No data returned' });
        }

        return updated;
      } catch (error) {
        track('UpdateAbrFilterFailed', {
          communityId,
          filterId: params.filterId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return null;
      }
    },
    [executeMutation, communityId]
  );

  const resetErrors = useCallback(() => setErrors(null), []);

  return useMemo(() => ({ updateFilter, loading, errors, resetErrors }), [updateFilter, loading, errors, resetErrors]);
}
